import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./HeroSection.module.css";
import { motion } from "framer-motion";

const MOBILE_FOCAL = "50% 38%";
const DESKTOP_FOCAL = "50% 50%";

const HERO_POSTER_NAME = "hero1";

const posterSet = (name: string) => ({
  avif: `/media/video-light/posters/${name}.avif`,
  webp: `/media/video-light/posters/${name}.webp`,
  jpg: `/media/video-light/posters/${name}.jpg`,
});

const srcOf = (level: "720" | "1080") =>
  `/media/video-light/mp4/${level}/hero-${level}.mp4`;

const isSplashActive = () =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-splash") === "1";

export default function HeroSection() {
  const [isWide, setIsWide] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(min-width:1200px)").matches
  );

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width:820px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mqWide = window.matchMedia("(min-width:1200px)");
    const mqMob = window.matchMedia("(max-width:820px)");

    const onChange = () => {
      setIsWide(mqWide.matches);
      setIsMobile(mqMob.matches);
    };

    mqWide.addEventListener?.("change", onChange);
    mqMob.addEventListener?.("change", onChange);

    return () => {
      mqWide.removeEventListener?.("change", onChange);
      mqMob.removeEventListener?.("change", onChange);
    };
  }, []);

  const connection =
    typeof navigator !== "undefined" && (navigator as any)?.connection
      ? (navigator as any).connection
      : null;

  const saveData = !!connection?.saveData;
  const effectiveType = connection?.effectiveType;
  const downlink =
    typeof connection?.downlink === "number" ? connection.downlink : undefined;

  const prefersReduced =
    typeof window !== "undefined" &&
    !!window.matchMedia("(prefers-reduced-motion: reduce)")?.matches;

  const autoplayAmbient =
    !prefersReduced && !saveData && (!effectiveType || effectiveType === "4g");

  const preloadMode: "none" | "metadata" | "auto" = autoplayAmbient
    ? "metadata"
    : "none";

  const videoRef = useRef<HTMLVideoElement>(null);

  const readyOnce = useRef(false);
  const startedOnce = useRef(false);

  const [objPos, setObjPos] = useState("50% 50%");
  const [firstFrameReady, setFirstFrameReady] = useState(false);
  const [showPoster, setShowPoster] = useState(true);

  const srcRef = useRef<string>("");

  const chooseLevel = useCallback(
    (): "720" | "1080" => {
      if (saveData) return "720";
      if (isMobile) return "720";
      if (effectiveType && effectiveType !== "4g") return "720";
      if (downlink !== undefined && downlink < 8) return "720";
      return isWide ? "1080" : "720";
    },
    [saveData, isMobile, effectiveType, downlink, isWide]
  );

  const markVideoReady = useCallback(() => {
    if (readyOnce.current) return;
    readyOnce.current = true;

    setFirstFrameReady(true);


    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShowPoster(false));
    });
  }, []);

  const ensureMetadata = useCallback(async (el: HTMLVideoElement) => {
    if (el.readyState >= 1) return;
    await new Promise<void>((resolve) => {
      const onMeta = () => resolve();
      el.addEventListener("loadedmetadata", onMeta, { once: true });
    });
  }, []);

  const waitFirstFramePresented = useCallback(
    (el: HTMLVideoElement) => {
      const anyEl = el as any;

      if (typeof anyEl.requestVideoFrameCallback === "function") {
        anyEl.requestVideoFrameCallback(() => {
          markVideoReady();
        });
        return;
      }

      const on = () => {
        if (el.videoWidth > 0) markVideoReady();
      };
      el.addEventListener("loadeddata", on, { once: true });
    },
    [markVideoReady]
  );

  const startFromBeginning = useCallback(async () => {
    const el = videoRef.current;
    if (!el || startedOnce.current) return;

    startedOnce.current = true;

    setShowPoster(true);
    setFirstFrameReady(false);
    readyOnce.current = false;

    try {
      await ensureMetadata(el);

 
      try {
        el.currentTime = 0;
      } catch {
     
      }

      const p = el.play();
      if (p?.catch) p.catch(() => {});

      waitFirstFramePresented(el);
    } catch {
      startedOnce.current = false;
    }
  }, [ensureMetadata, waitFirstFramePresented]);

  useEffect(() => {
    const el = videoRef.current;

    setObjPos(isMobile ? MOBILE_FOCAL : DESKTOP_FOCAL);

    if (!el) return;

    el.muted = true;
    el.playsInline = true;
    el.loop = true;
    el.preload = preloadMode;

    if (!autoplayAmbient) return;

    const level = chooseLevel();
    const nextSrc = srcOf(level);

    if (srcRef.current !== nextSrc) {
      srcRef.current = nextSrc;


      setShowPoster(true);
      setFirstFrameReady(false);
      readyOnce.current = false;
      startedOnce.current = false;

      el.src = nextSrc;

      try {
        el.load();
      } catch {
       
      }
    }


    if (!isSplashActive()) {
      startFromBeginning();
    }
  }, [autoplayAmbient, chooseLevel, isMobile, preloadMode, startFromBeginning]);


  useEffect(() => {
    if (!autoplayAmbient) return;

    if (!isSplashActive()) {
      startFromBeginning();
      return;
    }

    const root = document.documentElement;
    const obs = new MutationObserver(() => {
      if (!isSplashActive()) {
        obs.disconnect();
        startFromBeginning();
      }
    });

    obs.observe(root, { attributes: true, attributeFilter: ["data-splash"] });
    return () => obs.disconnect();
  }, [autoplayAmbient, startFromBeginning]);

  return (
    <section className={`${styles.hero} ${isMobile ? styles.mobile : ""}`}>
      <picture
        className={`${styles.heroPoster} ${
          showPoster ? "" : styles.heroPosterHide
        }`}
      >
        <source srcSet={posterSet(HERO_POSTER_NAME).avif} type="image/avif" />
        <source srcSet={posterSet(HERO_POSTER_NAME).webp} type="image/webp" />
        <img
          className={styles.poster}
          src={posterSet(HERO_POSTER_NAME).jpg}
          alt=""
          width={1920}
          height={1080}
          loading="eager"
          decoding="async"
        />
      </picture>

      {autoplayAmbient && (
        <video
          ref={videoRef}
          className={`${styles.video} ${
            firstFrameReady ? styles.videoReady : ""
          }`}
          style={{ objectPosition: objPos }}
          preload={preloadMode}
        />
      )}

      <div className={styles.overlay} />

      <motion.div
        className={styles.content}
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className={styles.title}>
          AVILA <span className={styles.gmbh}>PROJEKTMANAGEMENT</span>
        </h1>
        <p className={styles.subtitle}>
          Exzellenz in Immobilien&nbsp;&amp;&nbsp;Development
        </p>
      </motion.div>
    </section>
  );
}
